import * as THREE from "three";
import Experience from "../Experience";
import { sequenceBG } from "../sources";
import EventEmitter from '../Utils/EventEmitter';

export default class SequenceBG extends EventEmitter {
  constructor() {
    super();

    this.experience = new Experience();
    this.cameraWrapper = this.experience.camera;
    this.camera = this.experience.camera.instance;
    this.cameraPath = this.cameraWrapper.cameraPath;
    this.resources = this.experience.resources;
    // this.experience.renderer doesn't exist yet at construction time (World
    // is built before Renderer, so bgScene/bgCamera below exist in time for
    // Renderer's TwoScenePass) — loadFrame() below reads it lazily, inside
    // its load callback, which only fires once Renderer is guaranteed built.

    // The plate renders in its own scene with its own camera (reference:
    // bgScene/bgCam), composited behind the main scene with a depth clear
    // between the two passes — see Renderer.js. This is what lets the
    // plate hold an independent yaw/pitch orbit instead of billboarding to
    // the main camera's quaternion.
    this.bgScene = new THREE.Scene();
    this.bgCamera = new THREE.PerspectiveCamera(
      this.camera.fov,
      this.camera.aspect,
      1,
      10000,
    );
    // Same permanent overscan crop as the main camera (see
    // Camera.applyViewZoom) — both cameras share one crop rectangle so the
    // plate and the main scene zoom by exactly the same factor about the
    // same centre. Without this, a plate sized to exactly fill the
    // untilted frustum runs out of coverage the moment the camera orbits.
    this.applyViewZoom();

    this.currentIndex = 0;
    this.bufferWindow = 10;
    this.activeTextures = new Map();

    // preset.json plateDepth is already the verified /100 value (9.20),
    // read once here since it's a constant for the whole scene.
    this.plateDepth = Number(this.experience.sceneData.preset.plateDepth) / 100;

    const plate = this.experience.sceneData.preset.plate;
    this.plateScale = Number(plate.plateScale) / 100;
    this.plateOffX = Number(plate.plateOffX) / 100;
    this.plateOffY = Number(plate.plateOffY) / 100;
    // Source image's own aspect ratio (all 361 frames share it) — set once
    // the first frame loads. The mesh itself always matches the live
    // camera's frustum aspect (see updatePlate); this is only used to
    // crop the texture's UVs to that shape without stretching the photo
    // (applyCoverFitUV) — object-fit: cover, done in UV space.
    this.imageAspect = 0;
  }

  // Same crop as Camera.applyViewZoom, and same reasoning: the reference
  // frame passed to setViewOffset is built from the camera's OWN live
  // aspect rather than the fixed AE comp size, or the two axes zoom by
  // different amounts at a window aspect far from the comp's ~16:9 shape.
  applyViewZoom() {
    const sceneZoom = Number(this.experience.sceneData.preset.sceneZoom) / 100;
    const fullW = 1000;
    const fullH = fullW / this.bgCamera.aspect;
    const m = 1 / (1 + sceneZoom);
    const w = fullW * m;
    const h = fullH * m;

    this.bgCamera.setViewOffset(fullW, fullH, (fullW - w) / 2, (fullH - h) / 2, w, h);
    this.bgCamera.updateProjectionMatrix();
  }

  async warmup() {
    this.textures = await this.resources.loadImagesBlobs(
      sequenceBG,
      (progress) => {
        this.loadingProgress = progress;
      },
    );

    await this.updateBuffer(0);
  }

  loadFrame(index){
    return new Promise((resolve) => {
      this.resources.loaders.textureLoader.load(
        this.textures[index],
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          // Anisotropic filtering matters once the plate orbits (grazing
          // angles from tilt sway blur badly under plain bilinear). Capped
          // at 8 rather than the GPU's own max (getMaxAnisotropy() can be
          // 16) as ordinary good practice — visually indistinguishable from
          // the max on this content, cheaper to sample. Was briefly
          // suspected as the cause of a real FPS drop at the reveal, but
          // that turned out to be an unrelated per-tick full-texture GPU
          // re-upload in applyCoverFitUV below (see its comment) — ruled
          // out because dropping anisotropy further didn't change the drop
          // at all.
          texture.anisotropy = Math.min(
            8,
            this.experience.renderer.instance.capabilities.getMaxAnisotropy(),
          );
          if (!this.imageAspect) {
            this.imageAspect = texture.image.width / texture.image.height;
          }
          this.experience.renderer.instance.initTexture(texture);
          this.activeTextures.set(index, texture);

          resolve(texture);
        },
        undefined,
        () => resolve(null),
      );
    });
  }

  async updateBuffer(playheadIndex){
    const startBuffer = Math.max(0, playheadIndex - this.bufferWindow);
    const endBuffer = Math.min(this.textures.length - 1, playheadIndex + this.bufferWindow);

    const pending = [];
    for (let i = startBuffer; i <= endBuffer; i++) {
      if (!this.activeTextures.has(i)) {
        pending.push(this.loadFrame(i));
      }
    }

    if (pending.length > 0) {
      await Promise.all(pending);
    }

    const currentTexture = this.activeTextures.get(playheadIndex);
    if (currentTexture && this.bgMesh) {
      this.bgMesh.material.map = currentTexture;
      this.bgMesh.material.needsUpdate = true;
      // Freshly-swapped textures start at the default repeat/offset — crop
      // immediately rather than waiting a tick for updatePlate() to catch
      // up, so a frame change never shows one raw (uncropped) frame.
      this.applyCoverFitUV(this.bgMesh.scale.x / this.bgMesh.scale.y);
    }

    for (const [index, texture] of this.activeTextures.entries()) {
      if (index < startBuffer || index > endBuffer) {
        texture.dispose();
        this.activeTextures.delete(index);
      }
    }
  }

  createBGMesh(){
    const bgMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ map: this.activeTextures.get(0), depthWrite: false })
    );
    this.bgMesh = bgMesh;
    this.bgScene.add(bgMesh);
    this.bgScene.add(this.bgCamera);

    this.updatePlate(0, 0, 0);
  }

  // object-fit: cover, object-position: center, done in UV space — the
  // mesh is already sized to the frustum's own (live) aspect, so this
  // only needs to crop whichever axis the image overshoots on, never
  // stretch either axis.
  applyCoverFitUV(planeAspect){
    const texture = this.bgMesh.material.map;
    if (!texture || !(this.imageAspect > 0)) return;

    const repeatX = this.imageAspect > planeAspect ? planeAspect / this.imageAspect : 1;
    const repeatY = this.imageAspect > planeAspect ? 1 : this.imageAspect / planeAspect;

    // needsUpdate on a Texture forces Three.js to re-upload the full image
    // to the GPU on the next render (gl.texImage2D again, not just a UV
    // change) — this ran unconditionally every tick, which is harmless for
    // a small texture but re-uploads a full 1920x1080 frame every frame,
    // forever, once the playhead idles on one of the oversized clip-boundary
    // frames (see loadFrame's comment). Confirmed as the actual render:plate
    // cost — only flag it when repeat/offset actually changed (a genuine
    // texture or aspect change), not every tick.
    if (texture.repeat.x !== repeatX || texture.repeat.y !== repeatY) {
      texture.repeat.set(repeatX, repeatY);
      texture.offset.set((1 - repeatX) / 2, (1 - repeatY) / 2);
      texture.needsUpdate = true;
    }
  }

  // Exact port of the reference's updatePlate, with one deliberate
  // deviation: the mesh is sized to the LIVE camera aspect (object-fit:
  // cover via applyCoverFitUV) rather than the reference's plate.fit ===
  // "aspect" (mesh w/h locked to the source image's own aspect,
  // independent of the canvas). That's correct for the reference's
  // fixed-aspect video player, but on a responsive canvas a short/narrow
  // window needs a mesh shape the fixed ratio can't provide without
  // stretching the photo — see the comment below. bgCam's own yaw/pitch
  // orbit (about a pivot at the SAME distance as the main camera's pivot)
  // is what produces the parallax: relative motion between plate and
  // scene comes out to plateDepth exactly, a ratio set by the Plate depth
  // value alone.
  updatePlate(pivotDist, yaw, pitch){
    if (!this.bgMesh) return;

    const dist = Math.max(1, pivotDist * this.plateDepth);
    this.bgDistance = dist;

    this.bgCamera.fov = this.camera.fov;
    if (this.bgCamera.aspect !== this.camera.aspect) {
      // aspect only actually changes on resize — re-measure the crop
      // rectangle against the new aspect (see applyViewZoom) rather than
      // just updating the projection matrix, or the two axes zoom
      // unevenly at the new window shape.
      this.bgCamera.aspect = this.camera.aspect;
      this.applyViewZoom();
    } else {
      this.bgCamera.updateProjectionMatrix();
    }

    const fh = 2 * dist * Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2);
    const fw = fh * this.bgCamera.aspect;

    // Tiny extra safety margin on top of the frustum-fill size, for
    // sub-pixel/FXAA-edge-sampling slack — negligible in composition. The
    // real headroom against the tilt orbit revealing an edge comes from
    // applyViewZoom()'s permanent overscan crop (see constructor).
    const MARGIN = 1.015;

    // The reference's plate.fit === "aspect" locks the mesh's own w/h
    // ratio to the SOURCE image's aspect, independent of the live camera
    // aspect — correct for a fixed-aspect video player, but on a
    // responsive canvas a short/narrow window can end up needing a mesh
    // shape very different from that fixed ratio, well past what the
    // small oversize margin covers, and the image reads as stretched
    // once the plate can no longer fully cover the frustum without it.
    // Always covering the frustum from the LIVE camera aspect (object-fit:
    // cover) and letting applyCoverFitUV crop losslessly in UV space
    // keeps the photo itself undistorted at any window shape.
    const w = fw * this.plateScale * MARGIN;
    const h = fh * this.plateScale * MARGIN;

    this.bgMesh.position.set(this.plateOffX * fh, this.plateOffY * fh, -dist);
    this.bgMesh.scale.set(w, h, 1);
    this.applyCoverFitUV(fw / fh);

    if (yaw === 0 && pitch === 0) {
      this.bgCamera.position.set(0, 0, 0);
      this.bgCamera.quaternion.identity();
      return;
    }

    const qYaw = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    const qPitch = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), pitch);
    const q = qYaw.multiply(qPitch);

    const offset = new THREE.Vector3(0, 0, pivotDist).applyQuaternion(q);
    this.bgCamera.position.set(0, 0, -pivotDist).add(offset);
    this.bgCamera.quaternion.copy(q);
  }

  update(dt, playheadFrame){
    const pivotDist = this.cameraPath.getPivotDistance(playheadFrame);
    const yaw = this.cameraWrapper.tiltYaw ?? 0;
    const pitch = this.cameraWrapper.tiltPitch ?? 0;
    this.experience.debug?.watch("plate tilt", `yaw ${yaw.toFixed(4)} pitch ${pitch.toFixed(4)}`);
    this.updatePlate(pivotDist, yaw, pitch);

    if (!this.textures) return;

    const targetIndex = Math.round(
      THREE.MathUtils.clamp(playheadFrame, 0, this.textures.length - 1),
    );

    if (targetIndex !== this.currentIndex) {
      this.currentIndex = targetIndex;
      this.updateBuffer(targetIndex);
    }
  }

  resize(){
    // Mesh size/position are fully recomputed from camera fov/aspect each
    // tick in updatePlate() — nothing to reassert on resize.
  }
}
